/** Supported PDF page sizes for browser-side rendering. */
export type PdfPageSize = "Letter" | "A4";

interface PageGeometry {
  /** jsPDF format name. */
  format: string;
  /** Page width in CSS pixels at 96 dpi — the layout width the HTML is rendered at. */
  widthPx: number;
  /** Page width in points (1/72 in). */
  widthPt: number;
}

const pageGeometries: Record<PdfPageSize, PageGeometry> = {
  Letter: { format: "letter", widthPx: 816, widthPt: 612 },
  A4: { format: "a4", widthPx: 794, widthPt: 595.28 }
};

/**
 * Renders self-contained HTML (inline CSS, data-URI images, no scripts) to a PDF in the browser.
 *
 * The HTML is loaded into a hidden same-origin iframe so its styles neither leak into nor inherit
 * from the host page, then rasterized page by page with jsPDF's html renderer (html2canvas under
 * the hood). The iframe is sandboxed without script access, so document scripts never run, and
 * external URLs are not fetched by design — the contract is self-contained HTML. jsPDF is loaded
 * on first use so it stays out of the initial bundle.
 *
 * `marginPt` defaults to 0 so the document owns its own margins through its own CSS. No
 * `html2canvas.scale` is set: jsPDF then derives its raster scale as `width / windowWidth`, which
 * is exactly 0.75 (the CSS-px-to-point ratio at 96dpi/72pt) when `marginPt` is 0, since `width`
 * then equals `widthPt` against a `windowWidth` of `widthPx`. That makes every CSS px map to a true
 * 0.75pt/(1/96in) on every device, independent of devicePixelRatio — so pt and mm sizes in the
 * document are true size, identically, on both Safari and Chrome, retina and non-retina alike.
 */
export async function renderHtmlToPdf(html: string, pageSize: PdfPageSize, marginPt = 0): Promise<Blob> {
  const geometry = pageGeometries[pageSize];
  const iframe = document.createElement("iframe");
  iframe.setAttribute("sandbox", "allow-same-origin");
  iframe.setAttribute("aria-hidden", "true");
  iframe.className = "ops-app-pdf-render-frame";
  iframe.width = String(geometry.widthPx);
  iframe.height = "1";

  try {
    await new Promise<void>((resolve, reject) => {
      iframe.onload = () => resolve();
      iframe.onerror = () => reject(new Error("The PDF source document failed to load."));
      iframe.srcdoc = html;
      document.body.appendChild(iframe);
    });

    const renderDocument = iframe.contentDocument;

    if (renderDocument === null) {
      throw new Error("The PDF source document is not accessible.");
    }

    await renderDocument.fonts.ready;

    // jsPDF's .html() clones only the body into the render, so the head's styles must travel with
    // it. Move them into the body, then wrap the rest of the body in an all:initial container so
    // the host page's own stylesheet cannot leak into the render through inherited properties.
    for (const styleElement of renderDocument.head.querySelectorAll('style, link[rel="stylesheet"]')) {
      renderDocument.body.prepend(styleElement.cloneNode(true));
    }
    const isolationContainer = renderDocument.createElement("div");
    isolationContainer.style.cssText = "all:initial;display:block";
    while (renderDocument.body.firstChild !== null) {
      isolationContainer.append(renderDocument.body.firstChild);
    }
    renderDocument.body.append(isolationContainer);

    const { default: jsPDF } = await import("jspdf");
    const pdf = new jsPDF({ unit: "pt", format: geometry.format, orientation: "portrait" });

    await pdf.html(renderDocument.body, {
      autoPaging: "text",
      margin: marginPt,
      width: geometry.widthPt - marginPt * 2,
      windowWidth: geometry.widthPx,
      html2canvas: { useCORS: false, logging: false }
    });

    return pdf.output("blob");
  }
  finally {
    iframe.remove();
  }
}
